import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";
import axiosInstance from "../../axiosInstance";

const UserManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = React.useState<any[]>([]);
  const [search, setSearch] = React.useState("");
  const [roleFilter, setRoleFilter] = React.useState("all");
  const [loading, setLoading] = React.useState(false);
  const debounceRef = React.useRef<number | null>(null);

  const fetchUsers = React.useCallback(async (searchVal: string, role: string) => {
    setLoading(true);
    try {
      const params: any = {};
      if (searchVal) params.search = searchVal;
      if (role !== "all") params.role = role;
      const response = await axiosInstance.get("/api/admin/all-users", { params });
      setUsers(response.data.data);
    } catch (err) {
      enqueueSnackbar("Failed to fetch users", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  React.useEffect(() => {
    fetchUsers("", "all");
  }, [fetchUsers]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchUsers(val, roleFilter);
    }, 400);
  };

  const handleRoleFilterChange = (e: any) => {
    const val = e.target.value;
    setRoleFilter(val);
    fetchUsers(search, val);
  };

  const handleToggleRole = async (email: string, currentRole: string | null) => {
    const newRole = currentRole === "admin" ? null : "admin";
    try {
      await axiosInstance.put("/api/admin/update-user-role", {
        email,
        role: newRole,
      });
      enqueueSnackbar(
        newRole === "admin"
          ? `${email} is now an admin`
          : `Admin role removed from ${email}`,
        { variant: "success" }
      );
      fetchUsers(search, roleFilter);
    } catch (err: any) {
      if (err.response?.status === 409) {
        enqueueSnackbar("Cannot remove your own admin role", { variant: "error" });
      } else {
        enqueueSnackbar("Failed to update user role", { variant: "error" });
      }
    }
  };

  return (
    <div className="w-full p-4">
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by email"
          value={search}
          onChange={handleSearchChange}
          sx={{ minWidth: 300 }}
        />
        <Select
          size="small"
          value={roleFilter}
          onChange={handleRoleFilterChange}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Roles</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
          <MenuItem value="none">Non-Admin</MenuItem>
        </Select>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 4, gap: 1 }}>
          <CircularProgress />
          <span style={{ color: "#666" }}>Loading users...</span>
        </Box>
      ) : users.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, color: "#666" }}>
          No users found.
        </Box>
      ) : (
        <div className="bg-white overflow-x-auto">
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="user management table">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Site Count</TableCell>
                  <TableCell>Created On</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user: any) => {
                  const date = user.createdOn
                    ? new Date(user.createdOn).toLocaleString()
                    : "-";
                  return (
                    <TableRow
                      key={user.email}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row" className="break-all">
                        {user.email}
                      </TableCell>
                      <TableCell>{user.name || "-"}</TableCell>
                      <TableCell>{user.role === "admin" ? "Admin" : "-"}</TableCell>
                      <TableCell>{user.siteCount ?? 0}</TableCell>
                      <TableCell>{date}</TableCell>
                      <TableCell>
                        {user.role === "admin" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            sx={{ textTransform: "none" }}
                            onClick={() => handleToggleRole(user.email, user.role)}
                          >
                            Remove Admin
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: "none" }}
                            onClick={() => handleToggleRole(user.email, user.role)}
                          >
                            Make Admin
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
